import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IAiPromptTemplateListItem } from "../../../@types/admin/AdminAiPromptTemplate";
import { TableSorted } from "../../../components/TableSorted";
import {
  AI_PROMPT_TEMPLATES_QUERY_KEY,
  useAiPromptTemplates,
} from "../../../hooks/queries/admin/aiPromptTemplates";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  FiltersContainer,
  FiltersActions,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { AiPromptTemplateModal } from "./AiPromptTemplateModal";

const SEARCH_FIELDS: (keyof IAiPromptTemplateListItem)[] = [
  "name",
  "description",
  "promptKey",
  "modelName",
];

/**
 * AI Prompt Templates admin page (EN-5161), migrated from the Retool
 * AIPromptTemplates app. Lists the prompts that drive AI-generated clinical
 * summaries and edits their bodies.
 *
 * ⚠️ The `/Retool/AiPromptTemplates` endpoints exist on `dev`, not on the
 * `stag` line this branch is cut from — see `portal/retool-migration.md`.
 * Nothing here needs changing when the branch catches up; the shapes were
 * read from the controller on `origin/dev`.
 */
export function AdminAiPromptTemplates() {
  document.title = "AI prompt templates - Admin";

  const { openModal, closeModal } = useModal();
  const { aiPromptTemplates, isLoading, isError } = useAiPromptTemplates();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: aiPromptTemplates,
    searchFields: SEARCH_FIELDS,
  });

  const openTemplateModal = (template?: IAiPromptTemplateListItem) => {
    openModal(
      <AiPromptTemplateModal template={template} onClose={closeModal} />,
      { width: "min(880px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const columns: ColumnDef<IAiPromptTemplateListItem, string>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Name", enableSorting: true },
      { accessorKey: "promptKey", header: "Prompt key", enableSorting: true },
      {
        accessorKey: "description",
        header: "Description",
        enableSorting: true,
      },
      {
        // Retool showed the raw ward id and nothing for the general template.
        id: "wardId",
        header: "Ward",
        accessorFn: (row) =>
          row.wardId == null ? "All wards" : `Ward ${row.wardId}`,
        enableSorting: true,
      },
      {
        id: "version",
        header: "Version",
        accessorFn: (row) => String(row.version ?? ""),
        enableSorting: true,
      },
      {
        // `isActive` is a boolean, and TableSorted dashes a falsy cell — the
        // accessor renders the word so an inactive template reads as inactive.
        id: "isActive",
        header: "Status",
        accessorFn: (row) => (row.isActive ? "Active" : "Inactive"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.isActive ? "active" : "inactive"}>
            {row.original.isActive ? "Active" : "Inactive"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>AI prompt templates</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, description, prompt key, or model..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={() => openTemplateModal()}>
            Add prompt template
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the AI prompt templates. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[AI_PROMPT_TEMPLATES_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={items}
            onRowClick={(template: IAiPromptTemplateListItem) =>
              openTemplateModal(template)
            }
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "ai-prompt-templates",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
